import React, { useEffect, useState, Suspense } from 'react';
import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/firebase';
import vacantesService from '../services/vacantes.service';
import { PAYMENTS_ENABLED } from '@/config/features';
import { clearSessionData } from '../utils/tokenValidation';

// Lazy load view components
const Step4_Calendar = React.lazy(() => import('../views-react/candidate/application-flow/Step4_Calendar').then(m => ({ default: m.Step4_Calendar })));
const Step5_CalendarSuccess = React.lazy(() => import('../views-react/candidate/application-flow/Step5_CalendarSuccess').then(m => ({ default: m.Step5_CalendarSuccess })));
const Step6_ScheduleInterview = React.lazy(() => import('../views-react/candidate/application-flow/Step6_ScheduleInterview').then(m => ({ default: m.Step6_ScheduleInterview })));
const VacancyEditor = React.lazy(() => import('../views-react/recruiter/VacancyEditor').then(m => ({ default: m.VacancyEditor })));
const RecruiterWorkspace = React.lazy(() => import('../views-react/recruiter/RecruiterWorkspace').then(m => ({ default: m.RecruiterWorkspace })));
const VacantePublicView = React.lazy(() => import('../views-react/shared/VacantePublicView').then(m => ({ default: m.VacantePublicView })));
const VacancyDashboard = React.lazy(() => import('../views-react/recruiter/VacancyDashboard').then(m => ({ default: m.VacancyDashboard })));
const RecruiterHome = React.lazy(() => import('../views-react/recruiter/RecruiterHome').then(m => ({ default: m.RecruiterHome })));
const VacancyQuestionsReview = React.lazy(() => import('../views-react/recruiter/VacancyQuestionsReview').then(m => ({ default: m.VacancyQuestionsReview })));
const PendingApprovalView = React.lazy(() => import('../views-react/shared/PendingApprovalView').then(m => ({ default: m.PendingApprovalView })));
const CheckoutView = React.lazy(() => import('../views-react/recruiter/checkout/CheckoutView').then(m => ({ default: m.CheckoutView })));
const RecruiterSettings = React.lazy(() => import('../views-react/recruiter/RecruiterSettings').then(m => ({ default: m.RecruiterSettings })));
const VacancyMetrics = React.lazy(() => import('../views-react/recruiter/VacancyMetrics').then(m => ({ default: m.VacancyMetrics })));
const VacancyPublishedFichaPreview = React.lazy(() =>
  import('../views-react/recruiter/VacancyPublishedFichaPreview').then(m => ({
    default: m.VacancyPublishedFichaPreview,
  })),
);

// Loading Fallback
const LoadingFallback = () => React.createElement('div', { className: 'auth-loading-content', style: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
  React.createElement('div', { className: 'spinner-container' }, React.createElement('div', { className: 'spinner' }))
);

// Ruta a la que Google OAuth puede redirigir; reenvía a /home
export function GoogleAuthCallbackRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    // Capturar todos los parámetros actuales (status, code, state, jobId, vacante_id, etc.)
    const params = new URLSearchParams(searchParams.toString());

    // Si viene 'jobId', normalizarlo a 'vacante_id' para consistencia en /home
    const jobId = params.get('jobId');
    const looksLikeOAuthState = (v: string) => /^uid-[a-zA-Z0-9]+-[a-f0-9]+$/i.test(v);

    if (jobId && !looksLikeOAuthState(jobId)) {
      params.set('vacante_id', jobId);
      params.delete('jobId');
    }

    const vacanteId = params.get('vacante_id');
    const pathname = vacanteId ? `/home/apply/${vacanteId}` : '/home/dashboard';
    navigate({ pathname, search: vacanteId ? '' : params.toString() }, { replace: true });
  }, [navigate, searchParams]);
  return React.createElement('div', { className: 'auth-callback-loading' }, 'Procesando...');
}

// Wrapper para Step4_Calendar
export function Step4_CalendarRoute() {
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const jobIdFromQuery = searchParams.get('jobId') || jobId || null;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCandidateId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, []);

  if (!candidateId) {
    return React.createElement(LoadingFallback);
  }

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(Step4_Calendar, {
      jobId: jobIdFromQuery,
      candidateId: candidateId,
      onCalendarConnected: () => { },
    })
  );
}

// Wrapper para Step5_CalendarSuccess
export function Step5_CalendarSuccessRoute() {
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const jobIdFromQuery = searchParams.get('jobId') || jobId || null;

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(Step5_CalendarSuccess, {
      jobId: jobIdFromQuery || undefined,
    })
  );
}

// Wrapper para Step6_ScheduleInterview
export function Step6_ScheduleInterviewRoute() {
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const jobIdFromQuery = searchParams.get('jobId') || jobId || null;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCandidateId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, []);

  if (!candidateId) {
    return React.createElement(LoadingFallback);
  }

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(Step6_ScheduleInterview, {
      jobId: jobIdFromQuery,
      candidateId: candidateId,
      onAppointmentScheduled: () => { },
    })
  );
}

// Wrapper para VacancyEditor
export function VacancyEditorRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) {
    return React.createElement('div', null, 'Error: ID de vacante no encontrado');
  }

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(VacancyEditor, {
      vacanteId: id,
      initialData: undefined,
      onGoToDashboard: () => navigate('/recruiter'),
      onVacanteSaved: () => { },
    })
  );
}

// Wrapper para RecruiterWorkspace
export function RecruiterWorkspaceRoute() {
  const { vacanteId } = useParams<{ vacanteId?: string }>();
  const navigate = useNavigate();

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(RecruiterWorkspace, {
      vacanteId: vacanteId || undefined,
      onGoToDashboard: () => navigate('/recruiter'),
    })
  );
}

// Wrapper para VacantePublicView
export function VacantePublicViewRoute() {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return React.createElement('div', null, 'Error: ID de vacante no encontrado');
  }

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(VacantePublicView, { vacanteId: id })
  );
}

// Wrapper para RecruiterHome
export function RecruiterHomeRoute() {
  const navigate = useNavigate();

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(RecruiterHome, {
      onNavigateToDashboard: () => navigate('/recruiter'),
    })
  );
}

// Wrapper para VacancyDashboard
export function VacancyDashboardRoute() {
  const navigate = useNavigate();

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(VacancyDashboard, {
      onGoHome: () => navigate('/recruiter/new', { state: { reset: true } }),
      onEditVacante: (id: string) => navigate(`/recruiter/vacancies/${id}/edit`),
      onGoToWorkspace: (vacanteId: string) => navigate(`/recruiter/workspace/${vacanteId}`),
      onGoToQuestionsReview: (vacanteId: string) => navigate(`/recruiter/vacancies/${vacanteId}/questions`),
      onGoToMetrics: (vacanteId: string) => navigate(`/recruiter/metrics/${vacanteId}`),
      onGoToPublishedFichaPreview: (vacanteId: string) =>
        navigate(`/recruiter/vacancies/${vacanteId}/candidate-ficha-preview`),
      onLogout: async () => {
        try {
          await signOut(auth);
        } catch (e) {
          console.warn('Firebase signOut en logout:', e);
        }
        clearSessionData();
        window.location.href = '/';
      },
    })
  );
}

// Wrapper para VacancyMetrics
export function VacancyMetricsRoute() {
  const { vacanteId } = useParams<{ vacanteId?: string }>();
  const navigate = useNavigate();

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(VacancyMetrics, {
      vacanteId: vacanteId || undefined,
      onGoToDashboard: () => navigate('/recruiter'),
      onGoToWorkspace: (id: string) => navigate(`/recruiter/workspace/${id}`),
    })
  );
}

export function VacancyPublishedFichaPreviewRoute() {
  return React.createElement(
    Suspense,
    { fallback: React.createElement(LoadingFallback) },
    React.createElement(VacancyPublishedFichaPreview),
  );
}

// Wrapper para VacancyQuestionsReview
export function VacancyQuestionsReviewRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vacanteTitle, setVacanteTitle] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    const fetchVacante = async () => {
      try {
        const response = await vacantesService.getVacante(id);
        if (response.success && response.data) {
          const data = response.data as any;
          const datos = data.datos_extraidos || data;
          const title = datos.titulo_puesto || datos.titulo || undefined;
          setVacanteTitle(title);
        }
      } catch (err) {
        console.error('Error fetching vacante title:', err);
      }
    };
    fetchVacante();
  }, [id]);

  if (!id) {
    return React.createElement('div', null, 'Error: ID de vacante no encontrado');
  }

  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(VacancyQuestionsReview, {
      vacanteId: id,
      vacanteTitle: vacanteTitle,
      onGoBack: () => navigate(`/recruiter/vacancies/${id}/edit`),
      onGoToDashboard: () => navigate('/recruiter'),
      onQuestionsApproved: () => {
        navigate('/recruiter');
      },
    })
  );
}

// Cuenta de la organización sin rol asignado todavía
export function PendingApprovalRoute() {
  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(PendingApprovalView)
  );
}

// Wrapper para Checkout (Stripe)
export function CheckoutRoute() {
  // Pagos apagados: la ruta tampoco debe alcanzarse escribiendo la URL a mano.
  if (!PAYMENTS_ENABLED) {
    return React.createElement(Navigate, { to: '/recruiter', replace: true });
  }
  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(CheckoutView)
  );
}

// Wrapper para Configuración del reclutador
export function RecruiterSettingsRoute() {
  return React.createElement(Suspense, { fallback: React.createElement(LoadingFallback) },
    React.createElement(RecruiterSettings)
  );
}
